"use client";

import { useEffect, useState } from "react";
import { LEGAL_DOCS } from "@/components/legal";
import { LegalHeader } from "@/components/legal/LegalHeader";
import { LegalTableOfContents } from "@/components/legal/LegalTableOfContents";
import { LegalTabs } from "@/components/legal/LegalTabs";

function resolveSlugFromHash(hash: string): string {
  const cleaned = hash.replace(/^#/, "");
  if (!cleaned) return LEGAL_DOCS[0].slug;
  // Longest-prefix match: section IDs are doc-slug-prefixed.
  const match = [...LEGAL_DOCS]
    .sort((a, b) => b.slug.length - a.slug.length)
    .find((doc) => cleaned === doc.slug || cleaned.startsWith(`${doc.slug}-`));
  return match?.slug ?? LEGAL_DOCS[0].slug;
}

export default function LegalPage() {
  const [activeSlug, setActiveSlug] = useState<string>(LEGAL_DOCS[0].slug);

  useEffect(() => {
    setActiveSlug(resolveSlugFromHash(window.location.hash));
    const onHashChange = () => {
      setActiveSlug(resolveSlugFromHash(window.location.hash));
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  // After a section anchor is reached on first load, scroll it into view.
  useEffect(() => {
    const cleaned = window.location.hash.replace(/^#/, "");
    if (!cleaned || cleaned === activeSlug) return;
    const el = document.getElementById(cleaned);
    if (el) {
      // Wait a frame so the new doc has rendered.
      requestAnimationFrame(() => {
        el.scrollIntoView({ block: "start" });
      });
    }
  }, [activeSlug]);

  const handleTabSelect = (slug: string) => {
    history.replaceState(null, "", `#${slug}`);
    setActiveSlug(slug);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const active = LEGAL_DOCS.find((d) => d.slug === activeSlug) ?? LEGAL_DOCS[0];
  const ActiveDoc = active.Component;

  return (
    <div className="min-h-screen bg-white">
      <LegalHeader
        title="Privacy & Legal"
        subtitle="Logical Health’s privacy, terms, and compliance documents."
      />

      <LegalTabs
        docs={LEGAL_DOCS}
        activeSlug={activeSlug}
        onSelect={handleTabSelect}
      />

      <div className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
        <LegalTableOfContents sections={active.sections} />

        <main id="main" className="min-w-0">
          <div className="prose prose-lg max-w-none">
            <p className="text-lg font-medium text-gray-700 mb-8">
              <strong>Last Updated:</strong> {active.lastUpdated}
            </p>
            <ActiveDoc />
          </div>
        </main>
      </div>
    </div>
  );
}
